import { Col, Icon, Row, Spin } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ApplicationContext } from "../context";
import { BrandType } from "../types/enumerations/common";
import { Brand, Onload } from "../types/interfaces/onload";
import { DataUtils } from "../utilities";
import { getAuthenticationDetails } from "../utilities/authentication";

declare global {
    interface Window {
        DeveloperChatApp: any
    }
}

enum UserType {
    CHANNEL = "CHANNEL",
    COBRAND = "COBRAND",
    ADMIN = "ADMIN"
}

enum DestinationBrandType {
    CHANNEL = "CHANNEL",
    COBRAND = "COBRAND",
    SUB_BRAND = "SUB_BRAND"
}

type IDeveloperChatToolProps = {
    rsession: string,
    yccUrl: string,
    brandId: string,
    appId: string,
    brandName: string,
    locationUrl: string,
    region: string,
    environment: string,
    userName: string,
    userType?: string,
    destinationCobrandId?: string | undefined,
    destinationCobrandType?: string | undefined,
    destinationChannelId?: string | undefined,
    app?: string,
    extraParams?: string
}

type Token = {
    appId: string,
    brandId: number,
    brandName: string,
    environment: string,
    region: string,
    rsession: string,
    yccUrl: string,
    supportingChannelId?: number
}

interface IProps {
    brand: Brand & { channel?: Brand },
    extraParams?: string
    iFrame: {
        id: string,
        name: string
    },
    onDataChanged?: (isChanged: boolean, extraParams?: string) => void,
    onBrandChange?: (brand: Brand & { channel?: Brand }, extraParams?: string) => void,
}

const postForm = (path: string, params: any, method = "POST", target: string = "_blank") => {
    // Clean up any existing forms for the same target
    const existingForms = document.querySelectorAll(`form[target="${target}"]`);
    existingForms.forEach(form => form.remove());
    
    const form = document.createElement('form');
    form.method = method;
    form.action = path;
    form.target = target;
    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = key;
            hiddenField.value = params[key];
            form.appendChild(hiddenField);
        }
    }
    document.body.appendChild(form);
    form.submit();
}

const DeveloperChatTool: React.FC<IProps> = ({ brand, extraParams, iFrame, onDataChanged, onBrandChange }) => {
    const { permissions, onload } = useContext(ApplicationContext);

    const [isFrameLoading, setIsFrameLoading] = useState<boolean>(true);
    const [token, setToken] = useState<Token>();
    const lastLoadedBrandId = useRef<number | undefined>(undefined);
    const isIframeLoaded = useRef<boolean>(false);

    const loadDeveloperChatToolScript = (developerChatApiUrl: string) => {
        if (!window.DeveloperChatApp) {
            const urlOrigin = new URL(developerChatApiUrl)?.origin;
            const scriptUrl = urlOrigin + "/js/post-message.js"
            const script = document.createElement("script");
            script.src = scriptUrl;
            script.onload = () => {
                if (window.DeveloperChatApp) {
                    window.DeveloperChatApp.initialize({
                        "name": "myyodlee",
                        "app": "developerChat",
                        "attributes": {
                            "iframeId": iFrame.id
                        },
                        "eventHandlers": {
                            "error": function (eventData: any) {
                                window.dispatchEvent(new Event('unauthorisedAccess'));
                            },
                            "renew_session": function (eventData: any) {
                                window.dispatchEvent(new Event('resetTimer'));
                            }
                        }
                    })
                }
            }
            document.head.appendChild(script);
        } else {
            window.DeveloperChatApp.initialize({
                "name": "myyodlee",
                "app": "developerChat",
                "attributes": {
                    "iframeId": iFrame.id
                },
                "eventHandlers": {
                    "error": function (eventData: any) {
                        window.dispatchEvent(new Event('unauthorisedAccess'));
                    },
                    "renew_session": function (eventData: any) {
                        window.dispatchEvent(new Event('resetTimer'));
                    }
                }
            })
        }
    }

    const loadDeveloperChatTool = () => {
        if (onload?.developerChatApiUrl) {
            const authentication = getAuthenticationDetails(brand);
            const brandType = brand?.type?.id;
            const isAllCustomersSelected = [BrandType.YODLEE, BrandType.CHANNEL].includes(onload?.financialInstitution?.type?.id) && !brand?.id;

            if ((!isAllCustomersSelected || brand) && token) {
                // Check if iframe is already loaded for this brand
                const iframe = document.getElementById(iFrame.id) as HTMLIFrameElement;
                
                // Only reload if:
                // 1. Iframe hasn't been loaded yet (isIframeLoaded.current is false), OR
                // 2. Brand ID actually changed (not just object reference)
                const currentBrandId = brand?.id;
                const brandChanged = lastLoadedBrandId.current !== undefined && lastLoadedBrandId.current !== currentBrandId;
                
                // Reload if brand changed OR iframe hasn't been loaded yet
                // Note: We reload if isIframeLoaded.current is false, even if iframe has content,
                // because the content might be stale or from a previous session
                const shouldReload = brandChanged || !isIframeLoaded.current;
                
                if (shouldReload) {
                    setIsFrameLoading(true);
                    loadDeveloperChatToolScript(onload.developerChatApiUrl);

                    let props: IDeveloperChatToolProps = {
                        rsession: token.rsession,
                        yccUrl: token.yccUrl,
                        brandId: token.brandId + "",
                        appId: token.appId,
                        brandName: token?.brandName,
                        locationUrl: window.location.href,
                        region: token.region,
                        environment: token.environment,
                        userName: onload.user.userName,
                        userType: onload.user.role.name === "Admin" ? UserType.ADMIN : brandType === BrandType.CHANNEL ? UserType.CHANNEL : UserType.COBRAND,
                        destinationCobrandId: brand.id.toString(),
                        destinationCobrandType: brandType === BrandType.CHANNEL ? DestinationBrandType.CHANNEL : brandType === BrandType.SUB_BRAND ? DestinationBrandType.SUB_BRAND : DestinationBrandType.COBRAND,
                        destinationChannelId: brandType === BrandType.SUB_BRAND ? (token?.supportingChannelId?.toString() || "") : (brand?.channel?.id?.toString() || ""),
                        app: "developerChat"
                    };

                    if (extraParams) {
                        props.extraParams = extraParams;
                    }

                    postForm(onload.developerChatApiUrl, props, "POST", iFrame.name);
                } else {
                    // Iframe already loaded and brand hasn't changed, don't reload - just ensure it's ready
                    setIsFrameLoading(false);
                }
            }
        }
    }

    const findBrandById = (onload: Onload, brandId: number) => {
        if (onload.financialInstitution?.id === brandId) {
            return onload.financialInstitution;
        }

        let returnBrand: Brand | undefined = undefined;
        onload.financialInstitution?.brands?.find(brand => {
            if (brand.id === brandId) {
                returnBrand = brand;
                return true;
            }
            else if (brand.type?.id === BrandType.CHANNEL) {
                const foundSubBrand = brand.brands?.find(subBrand => {
                    if (subBrand.id === brandId) {
                        returnBrand = subBrand;
                        return true;
                    }
                })
                if (foundSubBrand) {
                    return true;
                }
            }
        });

        return returnBrand;
    }

    const getChannelForSubBrand = (brandId: number): Brand | undefined => {
        const brandType = onload?.financialInstitution.type.id;
        if (brandType === BrandType.YODLEE) {
            let channel: Brand | undefined = undefined;
            onload?.financialInstitution?.brands?.find(brand => {
                if (brand.type.id === BrandType.CHANNEL) {
                    const foundSubBrand = brand.brands?.find(subBrand => {
                        if (subBrand.id === brandId) {
                            channel = brand;
                            return true;
                        }
                    })
                    if (foundSubBrand) {
                        return true;
                    }
                }
            });
            return channel;
        } else if (brandType === BrandType.CHANNEL && onload?.financialInstitution.id !== brandId) {
            return onload?.financialInstitution as Brand;
        } else {
            return undefined;
        }
    }

    useEffect(() => {
        const authentication = getAuthenticationDetails(brand);

        DataUtils.configToolToken(authentication).get().then((token) => {
            setToken(token);
        }).catch((error) => {
            console.error("DeveloperChatTool - token error:", error);
        });
    }, [brand?.id]);

    useEffect(() => {
        const messageHandler = (event: any) => {
            // Let post-message.js handle renew_session and error events
            // We only handle developer-chat specific events here
            
            if (event?.data?.eventName === "developer_chat_data_change") {
                if (onDataChanged) {
                    const changeState: boolean = event?.data?.eventData?.changed;
                    onDataChanged(changeState);
                }
            }
            else if (event?.data?.eventName === "tenant_change") {
                if (onBrandChange) {
                    const brandId = parseInt(event?.data?.eventData?.destinationCobrandId);
                    const brand = onload && findBrandById(onload, brandId);

                    onBrandChange({
                        id: brand ? brand.id : brandId,
                        name: brand ? brand.name : "N/A",
                        type: brand ? brand.type : { id: BrandType.COBRAND, name: "COBRAND" },
                        brands: brand?.brands || [],
                        channel: getChannelForSubBrand(brandId)
                    }, event?.data?.eventData?.extraParams);
                }
            }
            // Handle iframe ready event
            else if (event?.data?.eventName === "iframe_ready" || event?.data?.eventName === "ready") {
                setIsFrameLoading(false);
                isIframeLoaded.current = true; // Mark iframe as loaded
            }
        }

        window.addEventListener("message", messageHandler);

        return () => window.removeEventListener("message", messageHandler);
    }, [onDataChanged, onBrandChange, onload]);

    useEffect(() => {
        if (token && brand?.id) {
            // Only reload if brand actually changed (not just extraParams or visibility)
            if (brand?.id !== lastLoadedBrandId.current) {
                lastLoadedBrandId.current = brand.id;
                isIframeLoaded.current = false; // Reset loaded flag when brand changes
                loadDeveloperChatTool();
                
                // Fallback timeout to hide spinner after 15 seconds
                const timeoutId = setTimeout(() => {
                    setIsFrameLoading(false);
                }, 15000);
                
                return () => clearTimeout(timeoutId);
            } else {
                // Brand hasn't changed - don't reload, just ensure iframe is ready
                // This prevents reload on minimize/maximize or extraParams changes
                if (isIframeLoaded.current) {
                    setIsFrameLoading(false);
                }
            }
        }
        // Only depend on token and brand.id - extraParams changes shouldn't trigger reload
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, brand?.id]);

    const handleIframeLoad = () => {
        // Check if iframe content is accessible and ready
        const iframe = document.getElementById(iFrame.id) as HTMLIFrameElement;
        if (iframe) {
            try {
                // Try to access iframe content (may fail due to CORS)
                const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDoc && iframeDoc.readyState === 'complete') {
                    setIsFrameLoading(false);
                    isIframeLoaded.current = true; // Mark iframe as loaded
                } else {
                    // If we can't access content, wait a bit and then hide spinner
                    setTimeout(() => {
                        setIsFrameLoading(false);
                        isIframeLoaded.current = true; // Mark iframe as loaded after timeout
                    }, 2000);
                }
            } catch (e) {
                // CORS error - iframe content not accessible, hide spinner after short delay
                setTimeout(() => {
                    setIsFrameLoading(false);
                    isIframeLoaded.current = true; // Mark iframe as loaded after timeout
                }, 2000);
            }
        } else {
            setIsFrameLoading(false);
        }
    };

    return (
        onload && permissions && token ?
            <div className="librechat-floater">
                <iframe
                    id={iFrame.id}
                    name={iFrame.name}
                    onLoad={handleIframeLoad}
                />
                {isFrameLoading && (
                    <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1000 }}>
                        <Spin size="large" />
                    </div>
                )}
            </div>
            : <div style={{ height: "100vh", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Spin size="large" />
            </div>
    )
}

export default DeveloperChatTool;
